// src/store/slices/userLinesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as con from "../../GlobalConstants";
import axios from "axios";
import { getRequestConfig } from "../logic/supportFunctions";
import * as sf from "../logic/supportFunctions"
import { extractErrorMessage } from "../../utils/errorFunctions";
import { createError, createNotification } from "../../templates/notifications/Notifications";


export const fetchUserLines = createAsyncThunk(
  "userLines/fetchUserLines",
  async (_, { rejectWithValue }) => {
    try {

      const config = getRequestConfig()

      let url = con.rest_base_url + con.user_lines_api;

      if (sf.surrogateActive()) {
        url =
          con.rest_base_url +
          con.staff_user_lines_api +
          `/${sf.getSurrogateUser()[con.ID]}`;
      }

      const res = await axios.get(url, config);

      return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error cargando líneas de negocio"));
    }
  }
);

// Creates a new User line
export const createUserLine = createAsyncThunk(
  "userLines/createUserLine",
  async (line, { rejectWithValue }) => {
    try {
      const config = getRequestConfig()
      line[con.USER] = sf.surrogateActive() ? sf.getSurrogateUser()[con.ID] : sf.getUser()[con.ID]

      const url = con.rest_base_url + `${sf.surrogateActive() ? con.staff_user_line_api : con.user_line_api}`

      const res = await axios.post(url, line, config)
      return res.data

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error creando la línea de negocio"))
    }
  }
)

// Deletes a user line
export const deleteUserLine = createAsyncThunk(
  "userLines/deleteUserLine",
  async (line, { rejectWithValue }) => {
    try {
      const config = getRequestConfig()

      const url = `${con.rest_base_url}${sf.surrogateActive() ? con.staff_user_line_api : con.user_line_api}${line[con.ID]}/`

      await axios.delete(url, config)
      return line

    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error eliminando la línea de negocio"))
    }
  }
)


const initialState = {
  lines: [],
  [con.USER_LINE_SAVED] : con.NOT_STARTED, // Status Object for saving the user's line 
  [con.USER_LINE_DELETED] : con.NOT_STARTED, // Status Object for deleting a user's line
  [con.USER_LINE_ADDED] : con.NOT_STARTED,  // Status Object for adding a user line
  lines_status: con.NOT_STARTED,
  adminLineComments: [],
};

const userLinesSlice = createSlice({
  name: con.REDUCER_USER_LINES,
  initialState,
  reducers: {
    resetUserLines: () => initialState,

    replaceUserLines: (state, action) => {
      state.lines = action.payload;
    },

    setUserLine: (state, action) => {
      const index = state.lines.findIndex(
        (line) => line[con.ID] === action.payload[con.ID]
      );

      if (index !== -1) {
        state.lines[index] = {
          ...state.lines[index],
          ...action.payload,
        };
      }
    },

    addUserLineComment: (state, action) => {
      state.adminLineComments.push(action.payload);
    },

    setAllUserLineComments: (state, action) => {
      state.adminLineComments = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // ================= FETCH LINES =================
      .addCase(fetchUserLines.pending, (state) => {
        state.lines_status = con.LOADING;
      })
      .addCase(fetchUserLines.fulfilled, (state, action) => {
        state.lines_status = con.OK;
        state.lines = action.payload;
      })
      .addCase(fetchUserLines.rejected, (state, action) => {
        state.lines_status = con.ERROR;
        createError(action.payload);
      })

      // ================= CREATE LINE =================
      .addCase(createUserLine.pending, (state) => {
        state[con.USER_LINE_ADDED] = con.LOADING
      })
      .addCase(createUserLine.fulfilled, (state, action) => {
        state[con.USER_LINE_ADDED] = con.OK
        state[con.REDUCER_USER_LINES].push(action.payload)
        createNotification("Línea de negocio creada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(createUserLine.rejected, (state, action) => {
        state[con.USER_LINE_ADDED] = con.ERROR;
        createError(action.payload);
      })

      // ================= DELETE LINE =================
      .addCase(deleteUserLine.pending, (state) => {
        state[con.USER_LINE_DELETED] = con.LOADING
      })
      .addCase(deleteUserLine.fulfilled, (state, action) => {
        state[con.USER_LINE_DELETED] = con.OK
        state[con.REDUCER_USER_LINES] = state[con.REDUCER_USER_LINES].filter(
          l => l[con.ID] !== action.payload[con.ID]
        )
        createNotification("Línea de negocio eliminada correctamente", con.NOTIFICATION_TIME_SHORT);
      })
      .addCase(deleteUserLine.rejected, (state, action) => {
        state[con.USER_LINE_DELETED] = con.ERROR;
        createError(action.payload);
      })
  }

});

export const {
  resetUserLines,
  replaceUserLines,
  setUserLine,
  addUserLineComment,
  setAllUserLineComments,
} = userLinesSlice.actions;

export default userLinesSlice.reducer;
